import { isSafeAssetUrl, isSafeEmail, isSafePhone } from '@/helpers/assetUrl';
import { cn } from '@/utils/cn';

export interface SupportContactsProps {
  email: string | null;
  phone: string | null;
  fallbackUrl: string | null;
  heading: string;
  emailLabel: string;
  phoneLabel: string;
  fallbackLabel: string;
  className?: string;
}

/**
 * Merchant support contacts. Every `href` is validated; an unsafe string is omitted, never rendered
 * as a link. `customDomain` is never used here.
 */
export function SupportContacts({
  email,
  phone,
  fallbackUrl,
  heading,
  emailLabel,
  phoneLabel,
  fallbackLabel,
  className,
}: SupportContactsProps) {
  const safeEmail = isSafeEmail(email) ? email : null;
  const safePhone = isSafePhone(phone) ? phone : null;
  const safeFallback = isSafeAssetUrl(fallbackUrl) ? fallbackUrl : null;

  if (safeEmail === null && safePhone === null && safeFallback === null) return null;

  return (
    <div className={cn('border-border bg-surface mt-1 flex flex-col gap-1.5 rounded-md border px-3 py-2.5', className)}>
      <p className="text-text text-sm font-medium">{heading}</p>
      <ul className="flex flex-col gap-1 text-sm">
        {safeEmail !== null && (
          <li>
            <a
              href={`mailto:${safeEmail}`}
              className="text-secondary-fg underline-offset-2 hover:underline"
            >
              {emailLabel}
            </a>
          </li>
        )}
        {safePhone !== null && (
          <li>
            <a
              href={`tel:${safePhone.replace(/[^\d+]/g, '')}`}
              className="text-secondary-fg underline-offset-2 hover:underline"
            >
              {phoneLabel}
            </a>
          </li>
        )}
        {safeFallback !== null && (
          <li>
            <a
              href={safeFallback}
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary-fg underline-offset-2 hover:underline"
            >
              {fallbackLabel}
            </a>
          </li>
        )}
      </ul>
    </div>
  );
}
